import { Button, Stack, useTheme } from "@mui/material";
import { Plus } from "lucide-react";
import type { FC } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardDetailEmptyState } from "web_myplann/Dashboard/Components/DashboardDetailEmptyState";
import { DashboardDetailRow } from "web_myplann/Dashboard/Components/DashboardDetailRow";
import { DashboardDetailSkeleton } from "web_myplann/Dashboard/Components/DashboardDetailSkeleton";
import { useCanAddDashboardItem } from "web_myplann/Dashboard/Hooks/useCanAddDashboardItem";
import { useDashboardDetailData } from "web_myplann/Dashboard/Hooks/useDashboardDetailData";
import { useDashboardDetailRows } from "web_myplann/Dashboard/Hooks/useDashboardDetailRows";
import { useNavigateDashboardAddItem } from "web_myplann/Dashboard/Hooks/useNavigateDashboardAddItem";
import type { DashboardType } from "web_myplann/Dashboard/Types/DashboardType";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";

type Props = {
	dashboard: DashboardType;
};

export const DashboardDetailV2Content: FC<Props> = ({ dashboard }) => {
	const { t } = useResource();
	const navigate = useNavigate();
	const { spacingTokens, iconSize } = useTheme();
	const canAddItem = useCanAddDashboardItem();
	const navigateToAddItem = useNavigateDashboardAddItem(dashboard.overviewType);

	const { portfolios, properties, reporting, isAnyLoading } =
		useDashboardDetailData(dashboard.overviewType);

	const { rows } = useDashboardDetailRows(
		portfolios?.data,
		properties?.data,
		reporting?.data,
	);

	if (isAnyLoading) {
		return <DashboardDetailSkeleton rowsPerCard={4} />;
	}

	const isEmpty = rows.length === 0;

	return (
		<Stack
			flex={1}
			pt={spacingTokens.sm}
			pb={spacingTokens.xl}
			gap={spacingTokens.sm}
		>
			{isEmpty && <DashboardDetailEmptyState />}
			{(!isEmpty || canAddItem) && (
				<Stack bgcolor="background.paper">
					{rows.map((row, index) => (
						<DashboardDetailRow
							key={row.id}
							row={row}
							isLast={index === rows.length - 1 && !canAddItem}
							onClick={() => navigate(row.navigateTo)}
						/>
					))}
					{canAddItem && (
						<Button
							variant="listCta"
							startIcon={<Plus size={iconSize.sm} />}
							onClick={navigateToAddItem}
						>
							{t(Resources.Dashboard.Detail.Add[dashboard.overviewType])}
						</Button>
					)}
				</Stack>
			)}
		</Stack>
	);
};
